import { api, state, toast, inspectorForm } from '../state.js';
import { esc, badge } from '/core/js/utils.js';
import '/core/js/components/change-password.js';
import './two-step.js';

/**
 * Your own account, from the console (chunk 4.8f): who you are here, your
 * two-step sign-in (<arc-two-step>, plan H.1) and your password. Everything
 * goes through /me, which takes the uid from the session.
 *
 * A directory account's name and email are the directory's: read-only here,
 * with a link to the same person on the Accounts page. The local break-glass
 * account has no directory entry, so its name and email are edited here. The
 * password form is core's <wdc-change-password> — the one account settings
 * shows — and /me/password changes the right password for either kind.
 */
class ArcMe extends HTMLElement {
  connectedCallback() {
    this.innerHTML = `
      <div class="page-header">
        <div class="detail-title"><h1>My account</h1></div>
      </div>
      <div data-slot="profile"><div class="loading-center"><div class="spinner"></div></div></div>
      <arc-two-step></arc-two-step>
      <wdc-change-password></wdc-change-password>`;

    this.querySelector('[data-slot="profile"]').addEventListener('click', e => {
      if (e.target.closest('[data-edit]')) this.#edit();
    });

    this.#load();
  }

  async #load() {
    let me;
    try {
      me = await api.me.get();
    } catch (err) {
      this.querySelector('[data-slot="profile"]').innerHTML = `<div class="alert alert-danger">${esc(err.message)}</div>`;
      return;
    }
    this.#render(me);

    this.querySelector('wdc-change-password').configure({
      save: (body) => api.me.changePassword(body),
      context: [me.login, me.first_name, me.last_name],
      intro: me.ldap_imported
        ? 'This is your directory password — the same one for your email, file shares, Wi-Fi and account settings. Changing it here changes it everywhere.'
        : 'This account exists only in ARC. Its password is not in the directory, and changing it changes nothing else.',
    });
  }

  #render(me) {
    this.me = me;
    const role = me.is_super_admin ? badge('info', 'Super Admin')
      : (me.permissions ?? []).includes('Admin') ? badge('active', 'Admin') : badge('none', 'User');
    const name = [me.first_name, me.last_name].filter(Boolean).join(' ');

    this.querySelector('[data-slot="profile"]').innerHTML = `
      <div class="card">
        <div class="card-header"><span>Profile</span>
          ${me.ldap_imported ? '' : '<button type="button" class="btn btn-sm btn-secondary" data-edit>Edit</button>'}</div>
        <div class="card-body">
          <dl class="kv">
            <dt>Login</dt><dd class="mono">${esc(me.login)}</dd>
            <dt>Name</dt><dd>${esc(name || '—')}</dd>
            <dt>Email</dt><dd>${esc(me.email || '—')}</dd>
            <dt>Role</dt><dd>${role}</dd>
            <dt>Signs in with</dt><dd>${me.ldap_imported ? 'the directory' : 'a password kept only in ARC (a local account)'}</dd>
          </dl>
          <p class="text-muted card-block">${me.ldap_imported
            ? `Your name and email come from the directory.
               <a href="#/accounts/${esc(encodeURIComponent(me.login))}">Open your directory account</a>
               to change them, like anyone else's.`
            : 'This account is not in the directory, so its name and email are kept here.'}</p>
        </div>
      </div>`;
  }

  #edit() {
    const me = this.me;
    inspectorForm({
      title: 'Edit profile',
      width: 420,
      body: `
        <div class="form-grid-2">
          <label class="form-field"><span>First name</span><input name="first_name" value="${esc(me.first_name ?? '')}"></label>
          <label class="form-field"><span>Last name</span><input name="last_name" value="${esc(me.last_name ?? '')}"></label>
        </div>
        <label class="form-field"><span>Email</span><input name="email" type="email" value="${esc(me.email ?? '')}"></label>`,
      onSubmit: async (f) => {
        const fresh = await api.me.update({
          first_name: f.first_name.value.trim(),
          last_name: f.last_name.value.trim(),
          email: f.email.value.trim(),
        });
        this.#render(fresh);
        window.dispatchEvent(new CustomEvent('user-changed', { detail: fresh }));
        toast('Profile saved', 'success');
      },
    });
  }
}

customElements.define('arc-me', ArcMe);
